import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import {
  CogIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ChartBarIcon,
  DocumentTextIcon,
  BanknotesIcon,
  IdentificationIcon,
  BellAlertIcon,
  SunIcon,
  CurrencyDollarIcon,
  ComputerDesktopIcon,
  ClipboardDocumentListIcon,
  ViewColumnsIcon,
  ArrowUturnLeftIcon,
  InformationCircleIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline'

// Category icons mapping
const CATEGORY_ICONS = {
  document_processing: DocumentTextIcon,
  risk_scoring: ShieldExclamationIcon,
  gaming_behavior: ChartBarIcon,
  aml_thresholds: ShieldCheckIcon,
  aml_screening: MagnifyingGlassIcon,
  facial_recognition: IdentificationIcon,
  alert_policy: BellAlertIcon,
  transaction_monitoring: BanknotesIcon,
  session_monitoring: ClockIcon,
  operating_hours: SunIcon,
  payout: CurrencyDollarIcon,
  kyc: IdentificationIcon,
  case_management: ClipboardDocumentListIcon,
  display: ViewColumnsIcon,
}

const CATEGORY_COLORS = {
  document_processing: 'from-sky-500 to-blue-500',
  risk_scoring: 'from-red-500 to-rose-500',
  gaming_behavior: 'from-amber-500 to-orange-500',
  aml_thresholds: 'from-emerald-500 to-teal-500',
  aml_screening: 'from-cyan-500 to-blue-500',
  facial_recognition: 'from-violet-500 to-purple-500',
  alert_policy: 'from-yellow-500 to-amber-500',
  transaction_monitoring: 'from-green-500 to-emerald-500',
  session_monitoring: 'from-indigo-500 to-blue-500',
  operating_hours: 'from-orange-500 to-red-500',
  payout: 'from-pink-500 to-rose-500',
  kyc: 'from-teal-500 to-cyan-500',
  case_management: 'from-slate-500 to-gray-500',
  display: 'from-gray-500 to-slate-500',
}

/** Fewer top-level buckets; each lists VenueConfigService category keys in display order */
const MAJOR_GROUPS = [
  {
    id: 'risk_compliance',
    label: 'Risk, AML & patron intelligence',
    description: 'Scores, gaming behaviour, AML thresholds, screening, biometrics, KYC',
    categoryKeys: [
      'risk_scoring',
      'gaming_behavior',
      'aml_thresholds',
      'aml_screening',
      'facial_recognition',
      'kyc',
    ],
  },
  {
    id: 'monitoring_operations',
    label: 'Monitoring & venue operations',
    description: 'Alert policy, cash/session monitoring, trading hours',
    categoryKeys: [
      'alert_policy',
      'transaction_monitoring',
      'session_monitoring',
      'operating_hours',
    ],
  },
  {
    id: 'documents_payouts',
    label: 'Documents & payouts',
    description: 'OCR, uploads, payout limits, per-payout risk scoring',
    categoryKeys: ['document_processing', 'payout', 'transaction_risk'],
  },
]

// ─── Unit chip — visually tag each setting by quantity kind ─────────────
const UNIT_STYLES = {
  AUD:     'bg-emerald-100 text-emerald-700',
  '%':     'bg-violet-100 text-violet-700',
  days:    'bg-sky-100 text-sky-700',
  months:  'bg-blue-100 text-blue-700',
  hours:   'bg-cyan-100 text-cyan-700',
  sec:     'bg-cyan-50 text-cyan-700',
  min:     'bg-cyan-50 text-cyan-700',
  years:   'bg-blue-100 text-blue-700',
  pts:     'bg-amber-100 text-amber-700',
  score:   'bg-yellow-100 text-yellow-800',
  count:   'bg-gray-100 text-gray-700',
  '24h':   'bg-indigo-100 text-indigo-700',
  ratio:   'bg-fuchsia-100 text-fuchsia-700',
  MB:      'bg-orange-100 text-orange-700',
  on_off:  'bg-pink-100 text-pink-700',
  json:    'bg-slate-200 text-slate-700',
  text:    'bg-slate-100 text-slate-600',
}

function UnitChip({ unit, title }) {
  if (!unit) return null
  const cls = UNIT_STYLES[unit] || 'bg-gray-100 text-gray-700'
  const label = unit === 'on_off' ? 'on/off' : unit
  return (
    <span title={title} className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>
      {label}
    </span>
  )
}

/**
 * Infer the quantity kind for a venue_configuration setting from its category,
 * key suffix, and (last resort) value shape. Each return value maps to a chip
 * in UNIT_STYLES above. Returning '' means "no chip" — most text fields.
 */
function unitFor(category, key, value) {
  // Boolean / JSON value sniff trumps key naming.
  if (value === true || value === false || value === 'true' || value === 'false') return 'on_off'
  if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) return 'json'
  if (typeof value === 'object' && value !== null) return 'json'

  // Category-specific overrides — these win against the generic suffix rules
  // below because the suffix is misleading for these keys.
  if (category === 'gaming_behavior') {
    if (/_loss_\d+_days$/.test(key)) return 'AUD'              // "$10k loss over 90 days" — value is AUD
    if (key.endsWith('_visits_per_week')) return 'count'
    if (key.endsWith('_stake_per_session')) return 'AUD'
    if (key === 'late_night_start_hour' || key === 'late_night_end_hour') return '24h'
    if (key === 'late_night_percentage_threshold') return '%'
  }
  if (category === 'risk_scoring') {
    if (key.startsWith('id_confidence_') && key.endsWith('_threshold')) return '%'
  }

  // Suffix-driven inference covers most of DEFAULTS without a per-category map.
  if (key.endsWith('_days') || key.endsWith('_period_days')) return 'days'
  if (key.endsWith('_months')) return 'months'
  if (key.endsWith('_hours') || key.endsWith('_window_hours')) return 'hours'
  if (key.endsWith('_seconds')) return 'sec'
  if (key.endsWith('_minutes')) return 'min'
  if (key.endsWith('_years') || key.endsWith('_period_years')) return 'years'
  if (key.endsWith('_percent') || key.endsWith('_pct')) return '%'
  if (key.endsWith('_points') || key.endsWith('_deduction') || key.endsWith('_score_cap')) return 'pts'
  if (key.endsWith('_count')) return 'count'

  // Category-aware rules for the ambiguous cases.
  switch (category) {
    case 'payout':
      return 'AUD'
    case 'aml_thresholds':
      if (key === 'structuring_min_transactions') return 'count'
      if (key === 'rapid_transaction_count_per_hour') return 'count'
      if (key === 'pep_score_threshold') return 'score'
      return 'AUD'
    case 'risk_scoring':
      if (key.startsWith('weight_')) return '%'
      if (key.endsWith('_threshold')) return 'score'
      if (key.startsWith('age_') || key.startsWith('membership_') || key.startsWith('id_confidence_')) return 'pts'
      if (key.startsWith('case_resolved_') || key.startsWith('smr_')) return 'pts'
      if (key.startsWith('pep_tier')) return 'pts'
      if (key.startsWith('occupation_')) return 'pts'
      return ''
    case 'aml_screening':
      if (key === 'cdd_tier.default_tier') return 'text'
      if (key.endsWith('_threshold') || key.endsWith('_confidence') || key.endsWith('_boost') || key.endsWith('_cap')) return '%'
      if (key.endsWith('_limit')) return 'count'
      return ''
    case 'facial_recognition':
      if (key === 'confidence_threshold') return 'ratio'
      if (key === 'max_candidates') return 'count'
      return ''
    case 'transaction_monitoring':
      if (key.startsWith('crt_level_')) return 'AUD'
      if (key === 'crt_tolerance') return 'ratio'
      return ''
    case 'session_monitoring':
      if (key.endsWith('_threshold_percent')) return '%'
      if (key === 'extended_session_seconds') return 'sec'
      if (key === 'min_avg_turnover_for_comparison') return 'AUD'
      if (key === 'min_sessions_for_comparison') return 'count'
      if (key.endsWith('_threshold') || key.endsWith('_count') || key.endsWith('_min_sessions')) return 'count'
      return ''
    case 'alert_policy':
      if (key === 'max_n_trigger') return 'count'
      if (key === 'default_mode') return 'text'
      return ''
    case 'operating_hours':
      return '24h'
    case 'document_processing':
      if (key === 'ocr_confidence_threshold') return '%'
      if (key === 'max_upload_size_mb') return 'MB'
      return ''
    case 'gaming_behavior':
      if (key.endsWith('_amount') || key.endsWith('_spend_jump_amount')) return 'AUD'
      if (key.endsWith('_changes') || key.endsWith('_changes_count')) return 'count'
      if (key.endsWith('_ratio')) return '%'
      return ''
    case 'kyc':
      if (key === 'kyc_mode') return 'text'
      if (key === 'max_failed_verification_attempts') return 'count'
      return ''
    case 'transaction_risk':
      if (key.endsWith('_threshold')) {
        if (key.startsWith('cash_ratio_')) return '%'
        if (key.startsWith('amount_band_')) return 'AUD'
        if (key.startsWith('level_') || key === 'investigate_threshold') return 'score'
      }
      if (key === 'cash_component_cap' || key === 'cash_component_near_cap') return 'AUD'
      if (key.startsWith('patron_high_combined_')) return 'score'
      return ''
    default:
      return ''
  }
}

const SOURCE_INDICATOR = {
  default: { color: 'bg-gray-300', ring: 'ring-gray-200', label: 'Hardcoded Default' },
  platform: { color: 'bg-blue-500', ring: 'ring-blue-200', label: 'Platform' },
  group: { color: 'bg-purple-500', ring: 'ring-purple-200', label: 'Group' },
  venue: { color: 'bg-green-500', ring: 'ring-green-200', label: 'Venue' },
}

/** Plain object only — `typeof null === 'object'` in JS, so null must be rejected explicitly. */
function normalizeCategories(value) {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return {}
  return value
}

function safeObjectEntries(obj) {
  if (obj == null || typeof obj !== 'object') return []
  return Object.entries(obj)
}

// Toggle switch
function Toggle({ enabled, onChange, disabled, size = 'md' }) {
  const sizes = {
    sm: { track: 'h-4 w-7', thumb: 'h-3 w-3', translate: 'translate-x-3' },
    md: { track: 'h-5 w-9', thumb: 'h-4 w-4', translate: 'translate-x-4' },
  }
  const s = sizes[size]
  return (
    <button type="button" onClick={() => !disabled && onChange(!enabled)}
      className={`relative inline-flex flex-shrink-0 ${s.track} border-2 border-transparent rounded-full cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${enabled ? 'bg-indigo-600' : 'bg-gray-200'}`}
      disabled={disabled}>
      <span className={`pointer-events-none inline-block ${s.thumb} rounded-full bg-white shadow transform transition duration-200 ${enabled ? s.translate : 'translate-x-0'}`} />
    </button>
  )
}

export default function VenueConfiguration() {
  const { can } = useAuth()
  const canManage = can('admin/venue-config', 'manage')
  const [categories, setCategories] = useState({})
  const [settings, setSettings] = useState({})
  const [venues, setVenues] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(null) // tracks which key is saving

  // Scope
  const [scope, setScope] = useState('platform')
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [selectedVenueId, setSelectedVenueId] = useState('')

  // UI
  const [expandedCategories, setExpandedCategories] = useState({})
  /** major section open by default (undefined = expanded); false = collapsed */
  const [expandedMajorGroups, setExpandedMajorGroups] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [editingKey, setEditingKey] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [auditHistory, setAuditHistory] = useState([])
  const [showAudit, setShowAudit] = useState(false)
  const [auditKey, setAuditKey] = useState('')

  useEffect(() => {
    loadCategories()
    loadGroups()
    loadVenues()
  }, [])

  useEffect(() => { loadSettings() }, [scope, selectedGroupId, selectedVenueId])

  const loadCategories = async () => {
    try {
      const res = await authFetch(getLocalApiUrl('/api/venue-config/categories'))
      if (!res.ok) {
        setCategories({})
        return
      }
      const data = await res.json()
      if (data.success) setCategories(normalizeCategories(data.categories))
      else setCategories({})
    } catch (_) {
      setCategories({})
    }
  }

  const loadSettings = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (selectedGroupId) params.set('groupId', selectedGroupId)
      if (selectedVenueId) params.set('venueId', selectedVenueId)
      const res = await authFetch(getLocalApiUrl(`/api/venue-config/resolved?${params}`))
      if (!res.ok) {
        setSettings({})
        setError(res.status === 403 || res.status === 401 ? 'Not allowed to load venue config for this scope.' : 'Failed to load settings')
        return
      }
      const data = await res.json()
      if (data.success) {
        const s = data.settings
        setSettings(s != null && typeof s === 'object' && !Array.isArray(s) ? s : {})
      } else setError(data.error)
    } catch (_) { setError('Failed to load settings') }
    finally { setLoading(false) }
  }

  const loadGroups = async () => {
    try {
      let res = await authFetch(getLocalApiUrl('/api/venue-config/groups'))
      if (res.status === 400) {
        res = await authFetch(getLocalApiUrl('/api/admin/feature-flags/tenants'))
      }
      if (!res.ok) {
        setGroups([])
        return
      }
      const data = await res.json()
      if (data.success) setGroups(Array.isArray(data.tenants) ? data.tenants : [])
      else setGroups([])
    } catch (_) {
      setGroups([])
    }
  }

  const loadVenues = async () => {
    try {
      const res = await authFetch(getLocalApiUrl('/api/venues'))
      const data = await res.json()
      if (data.success) setVenues(data.venues || data.data || [])
    } catch (_) {}
  }

  const saveSetting = useCallback(async (fullKey, value) => {
    const [category, ...keyParts] = fullKey.split('.')
    const key = keyParts.join('.')
    try {
      setSaving(fullKey)
      await authFetch(getLocalApiUrl('/api/admin/venue-config'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope,
          groupId: scope === 'platform' ? null : selectedGroupId,
          venueId: scope === 'venue' ? selectedVenueId : null,
          category, key,
          value: value !== undefined ? value : editValue,
        }),
      })
      setEditingKey(null)
      setEditValue('')
      loadSettings()
    } catch (_) { setError('Failed to save') }
    finally { setSaving(null) }
  }, [scope, selectedGroupId, selectedVenueId, editValue])

  const resetSetting = useCallback(async (fullKey) => {
    const [category, ...keyParts] = fullKey.split('.')
    const key = keyParts.join('.')
    const params = new URLSearchParams({ scope, category, key })
    if (selectedGroupId) params.set('groupId', selectedGroupId)
    if (selectedVenueId) params.set('venueId', selectedVenueId)
    await authFetch(getLocalApiUrl(`/api/admin/venue-config?${params}`), { method: 'DELETE' })
    loadSettings()
  }, [scope, selectedGroupId, selectedVenueId])

  const showAuditForKey = useCallback(async (fullKey) => {
    const [category, ...keyParts] = fullKey.split('.')
    const key = keyParts.join('.')
    const params = new URLSearchParams({ category, key, limit: '20' })
    if (selectedGroupId) params.set('groupId', selectedGroupId)
    const res = await authFetch(getLocalApiUrl(`/api/venue-config/audit?${params}`))
    const data = await res.json()
    if (data.success) { setAuditHistory(data.history || []); setAuditKey(fullKey); setShowAudit(true) }
  }, [selectedGroupId])

  // Group settings by category
  const groupedSettings = useMemo(() => {
    const grouped = {}
    const settingsObj = settings != null && typeof settings === 'object' && !Array.isArray(settings) ? settings : {}
    for (const [fullKey, entry] of Object.entries(settingsObj)) {
      const [category] = fullKey.split('.')
      if (!grouped[category]) grouped[category] = []
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        if (!fullKey.toLowerCase().includes(term) && !String(entry.value).toLowerCase().includes(term)) continue
      }
      grouped[category].push({ fullKey, ...entry })
    }
    return grouped
  }, [settings, searchTerm])

  const normalizedCatMap = useMemo(() => normalizeCategories(categories), [categories])

  const majorGroupSections = useMemo(() => {
    const knownKeys = Object.keys(normalizedCatMap)
    const assigned = new Set()
    const sections = []

    for (const mg of MAJOR_GROUPS) {
      const catKeys = mg.categoryKeys.filter((k) => {
        if (!knownKeys.includes(k)) return false
        if (searchTerm && !(groupedSettings[k] || []).length) return false
        return true
      })
      catKeys.forEach((k) => assigned.add(k))
      if (catKeys.length) sections.push({ ...mg, catKeys })
    }

    const otherKeys = knownKeys.filter((k) => !assigned.has(k)).filter((k) => {
      if (searchTerm && !(groupedSettings[k] || []).length) return false
      return true
    })
    if (otherKeys.length) {
      sections.push({
        id: 'other',
        label: 'Other',
        description: 'Additional configuration categories',
        catKeys: otherKeys.sort(),
      })
    }
    return sections
  }, [normalizedCatMap, groupedSettings, searchTerm])

  const toggleCategory = (cat) => setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }))
  const toggleMajorGroup = (id) =>
    setExpandedMajorGroups((p) => ({ ...p, [id]: !(p[id] !== false) }))

  // Determine the best input type for a value
  const getInputType = (fullKey, value) => {
    const strVal = String(value)
    if (strVal === 'true' || strVal === 'false') return 'boolean'
    if (strVal.startsWith('[') || strVal.startsWith('{')) return 'json'
    if (!isNaN(Number(strVal)) && strVal.trim() !== '') return 'number'
    return 'text'
  }

  const renderSettingControl = (item) => {
    const isEditing = editingKey === item.fullKey
    const isSaving = saving === item.fullKey
    const inputType = getInputType(item.fullKey, item.value)
    const displayKey = item.fullKey.split('.').slice(1).join('.')
    const src = SOURCE_INDICATOR[item.source] || SOURCE_INDICATOR.default
    const isOverridden = item.source === scope && scope !== 'default'

    return (
      <div
        key={item.fullKey}
        className={`flex items-center gap-3 pl-4 pr-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors ${isEditing ? 'bg-indigo-50/50 dark:bg-indigo-950/30' : ''}`}
      >
        {/* Source dot: same horizontal slot as category chevron (w-4), then spacer = icon cell in header */}
        <div className="w-4 flex-shrink-0 flex items-center justify-center" title={src.label}>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${src.color} ring-2 ${src.ring}`} />
        </div>
        <div className="w-7 flex-shrink-0" aria-hidden="true" />

        {/* Setting name + unit chip (lines up with category title text) */}
        <div className="min-w-0 flex-1 max-w-[280px]">
          <div className="flex items-center gap-1.5">
            <div className="text-sm text-gray-900 truncate" title={displayKey}>
              {displayKey.replace(/_/g, ' ')}
            </div>
            <UnitChip
              unit={unitFor(item.fullKey.split('.')[0], displayKey, item.value)}
              title="Quantity kind"
            />
          </div>
        </div>

        {/* Value control */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              {inputType === 'json' ? (
                <textarea value={editValue} onChange={e => setEditValue(e.target.value)}
                  className="text-sm border-gray-300 rounded-lg flex-1 font-mono h-16 resize-none focus:ring-indigo-500 focus:border-indigo-500" autoFocus />
              ) : (
                <input type={inputType === 'number' ? 'number' : 'text'} value={editValue} onChange={e => setEditValue(e.target.value)}
                  className="text-sm border-gray-300 rounded-lg flex-1 font-mono focus:ring-indigo-500 focus:border-indigo-500" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') saveSetting(item.fullKey); if (e.key === 'Escape') setEditingKey(null) }} />
              )}
              <button onClick={() => saveSetting(item.fullKey)} disabled={isSaving}
                className="p-1.5 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                <CheckCircleIcon className="h-4 w-4" />
              </button>
              <button onClick={() => setEditingKey(null)} className="p-1.5 text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : inputType === 'boolean' ? (
            <Toggle enabled={item.value === true || item.value === 'true'} disabled={!canManage}
              onChange={(val) => saveSetting(item.fullKey, String(val))} size="sm" />
          ) : !canManage ? (
            <span className={`text-sm font-mono truncate ${inputType === 'number' ? 'text-blue-700' : 'text-gray-700'}`}>
              {typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value)}
            </span>
          ) : (
            <button onClick={() => { setEditingKey(item.fullKey); setEditValue(typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value)) }}
              className="text-left group flex items-center gap-1.5 min-w-0">
              <span className={`text-sm font-mono truncate ${inputType === 'number' ? 'text-blue-700' : 'text-gray-700'}`}>
                {typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value)}
              </span>
              <PencilIcon className="h-3 w-3 text-gray-300 group-hover:text-indigo-500 flex-shrink-0 transition-colors" />
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {isOverridden && canManage && (
            <button onClick={() => resetSetting(item.fullKey)} title="Reset to parent default"
              className="p-1 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded transition-colors">
              <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
            </button>
          )}
          <button onClick={() => showAuditForKey(item.fullKey)} title="Change history"
            className="p-1 text-gray-300 hover:text-gray-500 hover:bg-gray-100 rounded transition-colors">
            <ClockIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CogIcon className="h-7 w-7 text-indigo-600" />
              Venue Configuration
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Operational settings that control how the system behaves. Inherits: Platform → Group → Venue.
            </p>
          </div>
          <button onClick={loadSettings} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}><XMarkIcon className="h-4 w-4" /></button>
        </div>
      )}

      {/* Scope selector */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          {/* Scope tabs */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Configuration Level</label>
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              {[
                { key: 'platform', label: 'Platform', icon: GlobeAltIcon },
                { key: 'group', label: 'Group', icon: BuildingOfficeIcon },
                { key: 'venue', label: 'Venue', icon: ComputerDesktopIcon },
              ].map(s => {
                const isActive = scope === s.key
                return (
                  <button key={s.key} onClick={() => { setScope(s.key); if (s.key === 'platform') { setSelectedGroupId(''); setSelectedVenueId('') } }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${isActive ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                    <s.icon className="h-4 w-4" />
                    {s.label}
                  </button>
                )
              })}
            </div>
          </div>

          {(scope === 'group' || scope === 'venue') && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Group</label>
              <select value={selectedGroupId} onChange={e => setSelectedGroupId(e.target.value)} className="text-sm border-gray-300 rounded-lg">
                <option value="">Select group...</option>
                {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
              </select>
            </div>
          )}

          {scope === 'venue' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Venue</label>
              <select value={selectedVenueId} onChange={e => setSelectedVenueId(e.target.value)} className="text-sm border-gray-300 rounded-lg">
                <option value="">Select venue...</option>
                {venues.map(v => <option key={v.venue_id} value={v.venue_id}>{v.name}</option>)}
              </select>
            </div>
          )}

          <div className="flex-1" />

          <div className="relative">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search settings..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-56 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
        </div>

        {/* Source legend */}
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-5 text-xs text-gray-500">
          {safeObjectEntries(SOURCE_INDICATOR).map(([key, cfg]) => (
            <span key={key} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${cfg.color}`} /> {cfg.label}
            </span>
          ))}
        </div>

        <p className="mt-3 text-xs text-gray-600 dark:text-gray-400 flex gap-2 leading-snug">
          <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-indigo-500 mt-0.5" aria-hidden />
          <span>
            <strong className="font-medium text-gray-700 dark:text-gray-300">Platform / Group / Venue</strong> is one hierarchy in the shared database: platform defaults apply to everyone;{' '}
            <strong className="font-medium text-gray-700 dark:text-gray-300">group</strong> overrides for your organisation;{' '}
            <strong className="font-medium text-gray-700 dark:text-gray-300">venue</strong> overrides for a single site. More specific wins. This is not a second copy of platform config—you are choosing{' '}
            <em>which level</em> you edit; runtime always resolves venue → group → platform → built-in defaults.
          </span>
        </p>
      </div>

      {/* Settings by category */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
      ) : (
        <div className="space-y-5">
          {majorGroupSections.map((section) => {
            const majorOpen = expandedMajorGroups[section.id] !== false
            const totalRows = section.catKeys.reduce((n, k) => n + (groupedSettings[k] || []).length, 0)
            return (
              <div
                key={section.id}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-900/50 overflow-hidden shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => toggleMajorGroup(section.id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {majorOpen ? (
                      <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{section.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{section.description}</div>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                    {section.catKeys.length} topic{section.catKeys.length !== 1 ? 's' : ''} · {totalRows} settings
                  </span>
                </button>

                {majorOpen && (
                  <div className="space-y-3 p-3 pt-2 border-t border-gray-200/90 dark:border-gray-700 bg-white dark:bg-gray-950">
                    {section.catKeys.map((catKey) => {
                      const catMeta = normalizedCatMap[catKey]
                      const meta = catMeta != null && typeof catMeta === 'object' ? catMeta : {}
                      const items = groupedSettings[catKey] || []
                      const isExpanded = expandedCategories[catKey]
                      const Icon = CATEGORY_ICONS[catKey] || CogIcon
                      const gradient = CATEGORY_COLORS[catKey] || 'from-gray-500 to-slate-500'
                      const overriddenCount = items.filter((i) => i.source === scope && scope !== 'default').length

                      return (
                        <div
                          key={catKey}
                          className="bg-white dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
                        >
                          <button
                            type="button"
                            onClick={() => toggleCategory(catKey)}
                            className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              )}
                              <div className={`p-1.5 rounded-lg bg-gradient-to-br ${gradient} text-white flex-shrink-0`}>
                                <Icon className="h-4 w-4" />
                              </div>
                              <div className="text-left">
                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  {meta.label || catKey}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{meta.description || ''}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {overriddenCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                  {overriddenCount} overridden
                                </span>
                              )}
                              <span className="text-xs text-gray-400 tabular-nums">{items.length}</span>
                            </div>
                          </button>

                          {isExpanded && items.length > 0 && (
                            <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-50 dark:divide-gray-800">
                              {items.map((item) => renderSettingControl(item))}
                            </div>
                          )}

                          {isExpanded && items.length === 0 && (
                            <div className="px-4 py-6 text-center text-sm text-gray-400 border-t border-gray-100 dark:border-gray-800">
                              No settings match the current search
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Audit History Modal */}
      {showAudit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAudit(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[70vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Change History</h3>
                <p className="text-xs text-gray-500 font-mono mt-0.5">{auditKey}</p>
              </div>
              <button onClick={() => setShowAudit(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {auditHistory.length === 0 ? (
                <div className="px-5 py-12 text-center text-sm text-gray-400">
                  <ClockIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  No changes recorded yet
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {auditHistory.map((entry, i) => (
                    <div key={i} className="px-5 py-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-500">{new Date(entry.created_at).toLocaleString()}</span>
                        <span className="text-xs text-gray-400">{entry.changed_by || 'system'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-mono text-red-600 bg-red-50 px-1.5 py-0.5 rounded text-xs">{entry.old_value}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-mono text-green-600 bg-green-50 px-1.5 py-0.5 rounded text-xs">{entry.new_value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
